import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import { useRillContext } from "../../../rill/RillReact/share/base";
import ActiveChips from "./components/ActiveChips";
import BusyTimes from "./components/BusyTimes";
import ConnectionPill from "./components/ConnectionPill";
import DoorEmpty from "./components/DoorEmpty";
import DoorsPanel from "./components/DoorsPanel";
import EventDrawer from "./components/EventDrawer";
import LinkButton from "./components/LinkButton";
import LogsFilters from "./components/LogsFilters";
import LogsStats from "./components/LogsStats";
import LogsTable from "./components/LogsTable";
import NotConnected from "./components/NotConnected";
import QuickToggle from "./components/QuickToggle";
import QuietBanner from "./components/QuietBanner";
import RetryLabel from "./components/RetryLabel";
import {
  buildQuery,
  defaultFilters,
  isFiltered,
  queryFromFilters,
} from "./door-access-utils";
import downloadCsv from "./download-csv";

const TOAST_MS = 6000;

const refresh = (body = {}) =>
  submit("/actions/refresh", { method: "GET", body });

const asState = (data) =>
  !data || data.error || data.forbidden
    ? { status: "error", data: null }
    : { status: "ready", data };

const Header = ({ status, gymName, coach, onOpenDoors }) => {
  const { t } = useTranslation("coaching-portal");
  return (
    <header className="door-header">
      <div>
        <h1>{t("doorAccess.title")}</h1>
        <p className="door-muted">
          {t(coach ? "doorAccess.coachSubtitle" : "doorAccess.subtitle", {
            gym: gymName,
          })}
        </p>
      </div>
      <div className="door-header__actions">
        <ConnectionPill status={status} />
        {status.can?.doors && status.connected && (
          <button type="button" data-button-type="text" onClick={onOpenDoors}>
            <span>{t("doorAccess.doorsAndLocations")}</span>
          </button>
        )}
      </div>
    </header>
  );
};

const LogsTab = ({ api, status, filters, doorOptions }) => {
  const { t } = useTranslation("coaching-portal");
  const { locals } = useRillContext();
  const { timeZone } = status;
  const [selected, setSelected] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState(null);
  const [resetKey, setResetKey] = useState(0);

  const logs = asState(locals.doorLogs);
  const summary = asState(locals.doorLogsSummary);
  const setFilters = (next) => refresh(queryFromFilters(next));
  const clearFilters = () => {
    setResetKey((key) => key + 1);
    setFilters(defaultFilters());
  };
  const reload = () => refresh();

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), TOAST_MS);
    return () => clearTimeout(timer);
  }, [toast]);

  const hasEvents = Boolean(status.lastEventAt) || (logs.data?.total || 0) > 0;
  const filtered = isFiltered(filters);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api("door-logs-export", {
        query: buildQuery(filters, timeZone),
      });
      if (res.status === 202) {
        const data = await res.json();
        setToast(t("doorAccess.export.async", { email: data.email }));
      } else if (res.ok) {
        const filename = await downloadCsv(res);
        setToast(
          t("doorAccess.export.done", {
            filename,
            count: (logs.data?.total || 0).toLocaleString("en-US"),
          })
        );
      } else {
        throw new Error(String(res.status));
      }
    } catch {
      setToast(t("doorAccess.export.failed"));
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      {status.connectionQuiet && <QuietBanner status={status} />}

      <LogsStats
        summary={summary}
        filters={filters}
        timeZone={timeZone}
        hasEvents={hasEvents}
        onToggleDenied={() =>
          setFilters({
            ...filters,
            result: filters.result === "denied" ? "all" : "denied",
            page: 1,
          })
        }
      />

      <LogsFilters
        key={resetKey}
        api={api}
        filters={filters}
        timeZone={timeZone}
        doors={doorOptions}
        unmatchedCount={logs.data?.unmatchedCount ?? 0}
        canExport={Boolean(status.can?.export) && hasEvents}
        exporting={exporting}
        onChange={setFilters}
        onExport={exportCsv}
      />

      <ActiveChips
        filters={filters}
        onChange={setFilters}
        onClear={clearFilters}
      />

      <LogsTable
        logs={logs}
        timeZone={timeZone}
        page={filters.page}
        hasEvents={hasEvents}
        filtered={filtered}
        onPage={(page) => setFilters({ ...filters, page })}
        onOpenEvent={setSelected}
        onShowUnmatched={() =>
          setFilters({ ...filters, unmatchedOnly: true, page: 1 })
        }
        onClear={clearFilters}
        onRetry={reload}
      />

      <EventDrawer
        api={api}
        event={selected}
        timeZone={timeZone}
        onClose={() => setSelected(null)}
      />
      {toast && <output className="door-toast">{toast}</output>}
    </>
  );
};

const DoorAccess = ({
  status,
  api,
  gymName,
  tab,
  filters,
  busyFilters,
  doorOptions,
}) => {
  const { t } = useTranslation("coaching-portal");
  const { locals } = useRillContext();
  const coach = status.role === "coach";
  const [doorsOpen, setDoorsOpen] = useState(false);

  if (status.forbidden) {
    return (
      <main className="door-access">
        <div className="door-card">
          <DoorEmpty
            big
            icon="/img/new-icons/lock.svg"
            title={t("doorAccess.noPermission.title")}
            text={t("doorAccess.noPermission.text")}
            actions={
              <LinkButton type="secondary" href="/coaching-portal/memberships">
                {t("doorAccess.noPermission.cta")}
              </LinkButton>
            }
          />
        </div>
      </main>
    );
  }

  if (status.error) {
    return (
      <main className="door-access">
        <div className="door-card">
          <DoorEmpty
            icon="/img/new-icons/warning-white.svg"
            tone="danger"
            title={t("doorAccess.states.errorTitle")}
            text={t("doorAccess.states.errorText")}
            actions={
              <LinkButton type="secondary" href="/coaching-portal/door-access">
                <RetryLabel />
              </LinkButton>
            }
          />
        </div>
      </main>
    );
  }

  return (
    <main className="door-access">
      <Header
        status={status}
        gymName={gymName}
        coach={coach}
        onOpenDoors={() => setDoorsOpen(true)}
      />
      <div className="door-tabs">
        <QuickToggle
          tabs
          label={t("doorAccess.title")}
          value={tab}
          options={[
            ...(status.can?.logs
              ? [{ value: "logs", label: t("doorAccess.tabs.logs") }]
              : []),
            { value: "busy", label: t("doorAccess.tabs.busy") },
          ]}
          onSelect={(value) => refresh({ tab: value })}
        />
        {coach && (
          <span
            className="door-pill door-pill--subtle"
            data-tone="default"
            title={t("doorAccess.aggregateViewTip")}
          >
            {t("doorAccess.aggregateView")}
          </span>
        )}
      </div>

      {!status.connected && <NotConnected />}
      {status.connected && tab === "logs" && (
        <LogsTab
          api={api}
          status={status}
          filters={filters}
          doorOptions={doorOptions}
        />
      )}
      {status.connected && tab === "busy" && (
        <BusyTimes api={api} status={status} filters={busyFilters} />
      )}
      {status.can?.doors && (
        <DoorsPanel
          api={api}
          doors={locals.gymDoors?.doors}
          open={doorsOpen}
          onClose={() => setDoorsOpen(false)}
          onSaved={() => refresh()}
        />
      )}
    </main>
  );
};

export default DoorAccess;
